// WHY AM I SUDDENLY LOOKING AT THE LOGIN SCREEN?
//
// THE CASE (device sitting, 2026-10-04). Carla: "401 on refresh, 401 on count, 401 on
// refresh. So I could not send, and therefore could not accept as a client. Being silently
// logged out mid-action is its own defect."
//
// It was silent by construction, in two places at once:
//   · contexts/CurrentUserContext's `aidey:auth:expired` handler called setCurrentUser(null)
//     and cleared localStorage, with no message anywhere.
//   · api/client.js marks the 401 `isAuthError` SO THAT the global rejection handler SKIPS
//     the toast — a deliberate suppression, added so a dead session would not spray toasts.
// Between them, the work on screen vanished and nothing explained it.
//
// A TOAST CANNOT FIX THIS, which is the whole reason this module exists. The session dying
// unmounts the entire authenticated tree and renders the login screen, so anything held in
// React state goes with it. The reason has to outlive the unmount, so it is written to
// sessionStorage and read once by the login screen.
//
// sessionStorage and not localStorage: this is about THIS tab's interrupted session. A
// reason left in localStorage would greet the person on a different tab days later.

const KEY = 'aidey_session_ended';

// What the person is told. One sentence about what happened, one about what it means for
// whatever they were doing — because the second is the part they actually need. It never
// says "try again": the action did not happen, and signing in is the next step.
const SENTENCE =
  'Your session ended, so you have been signed out. Anything you were part-way through was not saved or sent \u2014 sign in and do it again.';

export function noteSessionEnded(reason) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), reason: reason || 'expired' }));
  } catch (_) { /* private window, blocked storage \u2014 the sign-out still happens */ }
}

// Reads AND clears, so the message shows once rather than on every later visit to the
// login screen. Returns null when there is nothing to say.
export function takeSessionEndedMessage() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (raw) sessionStorage.removeItem(KEY);
  } catch (_) { return null; }
  if (!raw) return null;
  try {
    const note = JSON.parse(raw);
    // A stale note is worse than none: it would explain a sign-out that did not happen.
    // Fifteen minutes is long enough to survive a reload and short enough to be about now.
    if (!note || typeof note.at !== 'number' || Date.now() - note.at > 15 * 60 * 1000) return null;
    return SENTENCE;
  } catch (_) {
    return null;
  }
}

export { SENTENCE as SESSION_ENDED_SENTENCE };
